import type {
  StreamingFullPlaybackResponse,
  StreamingMasterUploadResponse,
  StreamingPreviewPlaybackResponse,
  VendorStreamingReleaseGetResponse,
} from '../../types/streaming';
import type { StorefrontThemeUploadResponse } from '../../types/storefront-theme-upload';
import { sha256HexFromBlob } from '../crypto/sha256-hex-from-blob';
import { iterateVendorChunkedUploadSlices } from '../streaming/chunked-streaming-upload';
import { resolveVendorStreamingMasterMimeType } from '../streaming/vendor-streaming-upload';
import { resolveStorefrontThemeUploadedImageUrl } from '../storefront-theme/vendor-storefront-theme-upload';
import {
  abortVendorChunkedUpload,
  completeVendorChunkedUpload,
  initVendorChunkedUpload,
  putVendorChunkedUploadChunk,
} from './vendor-chunked-upload';

export const backendUrl = __BACKEND_URL__ ?? '/';
export const publishableApiKey = __PUBLISHABLE_API_KEY__ ?? '';

/**
 * The client seam itself now lives in @sgftech/vendor-panel-core so the mobile
 * app can construct one with AsyncStorage. This module keeps the web-only
 * pieces — the Vite-injected URLs, and the File/FormData upload helpers below
 * that have no React Native equivalent — and re-exports the shared surface so
 * the ~70 existing `from "../../lib/client"` imports are untouched.
 *
 * setVendorClient() is called by src/main.tsx (and the vitest setup file), not
 * here: it must run before the first render, and importing this module is not
 * a reliable ordering guarantee.
 */
export {
  createVendorClient,
  setVendorClient,
  resetVendorClient,
  isTokenExpired,
  SessionExpiredError,
  fetchQuery,
  importProductsQuery,
  sdk,
} from '@sgftech/vendor-panel-core/lib/client'
export type {
  VendorClientConfig,
  FetchQueryFn,
} from '@sgftech/vendor-panel-core/lib/client'

import { isTokenExpired, sdk } from '@sgftech/vendor-panel-core/lib/client'

/** Retrieve the current auth token from web localStorage. Web-only helper. */
export const getAuthToken = (): string =>
  window.localStorage.getItem('medusa_auth_token') ?? ''

// useful when you want to call the BE from the console and try things out quickly
if (typeof window !== 'undefined') {
  (window as any).__sdk = sdk;
}

export const uploadFilesQuery = async (files: any[]) => {
  const bearer =
    (await window.localStorage.getItem('medusa_auth_token')) || '';
  const formData = new FormData();

  for (const { file } of files) {
    formData.append('files', file);
  }

  return await fetch(`${backendUrl}/vendor/uploads`, {
    method: 'POST',
    body: formData,
    headers: {
      authorization: `Bearer ${bearer}`,
      'x-publishable-api-key': publishableApiKey
    }
  })
    .then(res => res.json())
    .catch(() => null);
};

export type StreamingMasterUploadProgress = {
  loaded: number
  total: number
}

export type UploadStreamingMasterOptions = {
  onProgress?: (progress: StreamingMasterUploadProgress) => void
}

const STREAMING_CHUNK_PUT_CONCURRENCY = 2

const getErrorStatus = (error: unknown): number | undefined => {
  if (error && typeof error === 'object' && 'status' in error) {
    const s = (error as { status: unknown }).status
    return typeof s === 'number' ? s : undefined
  }
  return undefined
}

/** Re-throw or redirect for vendor 401 responses (chunked upload, etc.). */
const rethrowIfVendorUnauthorized = (error: unknown, bearer: string): void => {
  if (getErrorStatus(error) !== 401) {
    return
  }

  if (isTokenExpired(bearer)) {
    localStorage.removeItem('medusa_auth_token');
    window.location.href = '/login?reason=Unauthorized';
    throw new Error('Unauthorized');
  }

  const message =
    error instanceof Error ? error.message : 'Unauthorized';
  throw {
    type: 'NO_PERMISSION' as const,
    message: message || 'Unauthorized',
  };
};

const runVendorChunkedStep = async <T>(
  bearer: string,
  fn: () => Promise<T>
): Promise<T> => {
  try {
    return await fn();
  } catch (error: unknown) {
    rethrowIfVendorUnauthorized(error, bearer);
    throw error;
  }
};

const streamingMasterInitBody = (file: File, sha256Hex: string) => ({
  filename: file.name.slice(0, 512),
  mimeType: resolveVendorStreamingMasterMimeType(file),
  totalBytes: file.size,
  sha256Hex,
});

export const uploadStreamingMasterQuery = async (
  file: File,
  options?: UploadStreamingMasterOptions
): Promise<StreamingMasterUploadResponse> => {
  const bearer = (await window.localStorage.getItem('medusa_auth_token')) || '';

  let sessionUploadId: string | null = null;

  try {
    const sha256Hex = await sha256HexFromBlob(file);

    const init = await runVendorChunkedStep(bearer, () =>
      initVendorChunkedUpload(
        backendUrl,
        publishableApiKey,
        bearer,
        streamingMasterInitBody(file, sha256Hex)
      )
    );

    sessionUploadId = init.upload_id;

    const slices = Array.from(iterateVendorChunkedUploadSlices(file, init));
    let loaded = 0;
    const total = file.size;

    for (let i = 0; i < slices.length; i += STREAMING_CHUNK_PUT_CONCURRENCY) {
      const batch = slices.slice(i, i + STREAMING_CHUNK_PUT_CONCURRENCY);
      await runVendorChunkedStep(bearer, () =>
        Promise.all(
          batch.map((slice) =>
            putVendorChunkedUploadChunk(
              backendUrl,
              publishableApiKey,
              bearer,
              sessionUploadId!,
              slice.index,
              slice.blob
            )
          )
        )
      );

      for (const slice of batch) {
        loaded += slice.size;
      }

      if (total > 0) {
        options?.onProgress?.({ loaded, total });
      }
    }

    const complete = await runVendorChunkedStep(bearer, () =>
      completeVendorChunkedUpload(
        backendUrl,
        publishableApiKey,
        bearer,
        sessionUploadId!,
        { destination: 'streaming_master' }
      )
    );

    if (complete.kind !== 'streaming_master') {
      throw new Error(
        `Unexpected upload result kind "${complete.kind}"; expected streaming_master.`
      );
    }

    sessionUploadId = null;

    return {
      object_key: complete.object_key,
      mime_type: complete.mime_type,
      byte_size: complete.byte_size,
    };
  } finally {
    if (sessionUploadId) {
      await abortVendorChunkedUpload(
        backendUrl,
        publishableApiKey,
        bearer,
        sessionUploadId
      );
    }
  }
};

export const uploadStorefrontThemeImageQuery = async (
  file: File
): Promise<string> => {
  const bearer = (await window.localStorage.getItem('medusa_auth_token')) || '';

  const formData = new FormData();
  formData.append('files', file, file.name);

  const response = await fetch(`${backendUrl}/vendor/storefront-theme/upload`, {
    method: 'POST',
    body: formData,
    headers: {
      authorization: `Bearer ${bearer}`,
      'x-publishable-api-key': publishableApiKey
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);

    if (response.status === 401) {
      if (isTokenExpired(bearer)) {
        localStorage.removeItem('medusa_auth_token');
        window.location.href = '/login?reason=Unauthorized';
        return Promise.reject(new Error('Unauthorized'));
      }

      throw {
        type: 'NO_PERMISSION',
        message:
          (errorData && typeof errorData === 'object' && 'message' in errorData
            ? String((errorData as { message?: unknown }).message)
            : '') || 'Unauthorized'
      };
    }

    const message =
      errorData && typeof errorData === 'object' && 'message' in errorData
        ? String((errorData as { message?: unknown }).message)
        : 'Server error';

    const error = new Error(message);
    (error as Error & { status: number }).status = response.status;
    throw error;
  }

  const payload = (await response.json()) as StorefrontThemeUploadResponse;
  const uploaded = Array.isArray(payload.files) ? payload.files[0] : undefined;

  return resolveStorefrontThemeUploadedImageUrl(uploaded);
};


// Streaming request helpers moved to @sgftech/vendor-panel-core alongside the
// hook that calls them — they are plain fetchQuery wrappers with no browser
// dependency. Re-exported here so existing `from "../../lib/client"` imports
// keep working.
export {
  fetchVendorStreamingReleaseQuery,
  mintVendorStreamingPreviewQuery,
  mintVendorStreamingSessionQuery,
} from '@sgftech/vendor-panel-core/lib/streaming/streaming-queries'
